// Autenticação do GPS Drive Backend — login/logout/recuperação de sessão
// via JWT próprio (NUNCA a sessão do Traccar, ver src/services/auth.service.ts
// para aquela). Fica fora de gpsdrive-client.ts (que só sabe anexar
// Authorization/renovar token) pelo mesmo motivo de auth.service.ts (Traccar)
// ficar fora de lib/api/client.ts: login/logout/refresh têm contrato próprio
// (cookie HttpOnly, sem Bearer na primeira chamada).
import {
  GPSDRIVE_API_URL,
  GpsDriveApiError,
  gpsDrivePost,
  refreshGpsDriveSession,
  setGpsDriveAccessToken,
} from "@/lib/api/gpsdrive-client";

export interface TraccarSessionSwitchResult {
  originalCookie: string | null;
}

export interface GpsDriveUser {
  id: string;
  name: string;
  email: string;
  companyId: string;
  role: string | null;
  // "Grupo do usuário" ("usuario" = rótulo "Cliente" no admin) — pedido
  // 2026-09-10, usado pra decidir se a aba "Criar usuário" (Perfil do PWA)
  // aparece (só Cliente pode ter sub-usuários, ver
  // gpsdrive-sub-users.service.ts).
  accountType: string;
  // Quem criou este usuário — null pro titular de verdade. Um sub-usuário
  // criado por um Cliente herda accountType "usuario" do titular (ver
  // SubUsersService#create no backend), então accountType sozinho não
  // basta pra saber se é o titular ou um sub-usuário dele — precisa deste
  // campo junto (bug real 2026-09-14: sub-usuário via "Criar usuário"
  // continuava vendo a própria aba "Criar usuário").
  createdByUserId: string | null;
  // Admin/funcionário que entrou com senha fraca — a app inteira fica atrás
  // da tela obrigatória de troca (ForcePasswordChange) e o backend recusa
  // tudo fora de /auth/* com 403 PASSWORD_CHANGE_REQUIRED.
  mustChangePassword?: boolean;
  createdAt: string;
  updatedAt: string;
}

interface AuthResponse {
  accessToken: string;
  tokenType: "Bearer";
  expiresIn: number;
  user: GpsDriveUser;
}

function requireBaseUrl(): string {
  if (!GPSDRIVE_API_URL) {
    throw new GpsDriveApiError("VITE_GPSDRIVE_API_URL não configurada", 0);
  }
  return GPSDRIVE_API_URL;
}

export const gpsDriveAuthService = {
  // Login "de gestão" — chamado junto do login do Traccar na mesma tela
  // (ver AuthProvider), nunca sozinho. Falhar aqui não deve impedir o
  // login no Traccar (rastreamento continua funcionando mesmo se o GPS
  // Drive Backend estiver fora do ar) — quem decide isso é o AuthProvider,
  // este serviço só propaga o erro.
  async login(email: string, password: string): Promise<GpsDriveUser> {
    const res = await fetch(`${requireBaseUrl()}/api/v1/auth/login`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({ email, password }),
    });

    if (!res.ok) {
      const detail = (await res.json().catch(() => undefined)) as { message?: string; error?: string } | undefined;
      throw new GpsDriveApiError(detail?.message ?? `Erro ${res.status} ao autenticar`, res.status, detail?.error);
    }

    const data = (await res.json()) as AuthResponse;
    setGpsDriveAccessToken(data.accessToken);
    return data.user;
  },

  // Recupera a sessão a partir do refresh token (cookie HttpOnly) — usado
  // no boot da aplicação (F5, aba nova), já que o access token só vive em
  // memória e não sobrevive a um reload. null = sem sessão válida (nunca
  // logou, ou o refresh token expirou/foi revogado) — não é um erro real.
  //
  // Passa pelo single-flight de refreshGpsDriveSession (gpsdrive-client.ts)
  // em vez de um fetch próprio — nunca duplicar a chamada a /auth/refresh
  // aqui, isso já causou revogação indevida da sessão (duas chamadas
  // concorrentes ao boot rotacionando o mesmo refresh token, ver comentário
  // em gpsdrive-client.ts).
  // Troca da própria senha (tela obrigatória de admin/funcionário). Devolve
  // tokens novos já sem a trava mustChangePassword.
  async changePassword(input: { currentPassword: string; newPassword: string; phone?: string }): Promise<GpsDriveUser> {
    const data = await gpsDrivePost<AuthResponse>("/api/v1/auth/change-password", input);
    setGpsDriveAccessToken(data.accessToken);
    return data.user;
  },

  async recoverSession(): Promise<GpsDriveUser | null> {
    const result = await refreshGpsDriveSession();
    return (result?.user as GpsDriveUser | undefined) ?? null;
  },

  async logout(): Promise<void> {
    try {
      await fetch(`${requireBaseUrl()}/api/v1/auth/logout`, {
        method: "POST",
        credentials: "include",
      });
    } finally {
      setGpsDriveAccessToken(null);
    }
  },

  // "Logar como" (Controle de Usuário, painel expandido de Usuários) —
  // troca só o access token/identidade do GPS Drive Backend em memória
  // pelo do usuário alvo; NUNCA mexe no refresh token/cookie do
  // administrador (ver AuthService#impersonate no backend), então
  // gpsDriveAuthService.recoverSession() continua trazendo de volta a
  // sessão do administrador depois (ver AuthProvider#stopImpersonating).
  // Autenticado (Bearer do próprio admin) — por isso gpsDrivePost, não um
  // fetch cru como login()/logout() acima.
  async impersonate(userId: string): Promise<GpsDriveUser> {
    const data = await gpsDrivePost<AuthResponse>(`/api/v1/auth/impersonate/${userId}`);
    setGpsDriveAccessToken(data.accessToken);
    return data.user;
  },

  // "Logar como" — parte 2, troca de VERDADE a sessão de RASTREAMENTO do
  // Traccar (mapa/lista de veículos restrita ao alvo), não só a sessão de
  // gestão acima (ver TraccarSessionSwitchService no backend). Devolve
  // `originalCookie`: o cookie do Traccar que o PRÓPRIO administrador já
  // tinha antes desta chamada — precisa ser guardado em memória (nunca
  // localStorage, mesmo cuidado do access token) e mandado de volta em
  // restoreTraccarSession() ao encerrar a impersonação, senão não tem
  // como voltar pra sessão de rastreamento do administrador.
  async switchTraccarSession(userId: string): Promise<TraccarSessionSwitchResult> {
    return gpsDrivePost<TraccarSessionSwitchResult>(`/api/v1/auth/impersonate/${userId}/traccar-session`);
  },

  // Restaura o cookie do Traccar do administrador — `cookie` é sempre o
  // `originalCookie` devolvido por switchTraccarSession() minutos antes,
  // nunca inventado aqui. `cookie: null` é válido (administrador não tinha
  // sessão do Traccar pra restaurar) e é um no-op no backend.
  async restoreTraccarSession(cookie: string | null): Promise<void> {
    await gpsDrivePost<void>("/api/v1/auth/impersonate/restore-traccar-session", { cookie });
  },
};
